/**
 * Catálogo dos relatórios da plataforma.
 *
 * É a única fonte de verdade da navegação de `/relatorios`: o menu lateral, a home
 * e os testes leem daqui. Relatório novo entra adicionando um item — nada de repetir
 * rota e rótulo em três arquivos.
 *
 * Os grupos são por área justamente porque a lista vai crescer para outros clientes
 * e frentes; o que separa um grupo do outro é a origem do dado, não a tecnologia.
 */

/** Qual flag do `/auth/me/` libera o item. Cada frente tem a sua (B03, painéis). */
export type ChaveGate = 'dashboard' | 'seop';

export interface ItemRelatorio {
  rota: string;
  rotulo: string;
  /** Uma linha: o que a pessoa vê ao abrir. Aparece no card da home. */
  descricao: string;
  /** Material Symbols, igual ao resto do app. */
  icone: string;
  gate: ChaveGate;
  /**
   * Item já combinado mas ainda não no ar. Aparece na lista sem link, para a
   * reunião saber o que vem — e para ninguém clicar e cair em 404.
   */
  emBreve?: boolean;
}

export interface GrupoRelatorios {
  id: string;
  titulo: string;
  /** Explica a origem do dado do grupo. */
  descricao: string;
  itens: ItemRelatorio[];
}

export const GRUPOS_RELATORIOS: GrupoRelatorios[] = [
  {
    id: 'equiplex',
    titulo: 'Equiplex',
    descricao: 'Lidos direto da réplica do Datasul, sem planilha no meio.',
    itens: [
      {
        rota: '/paineis/equiplex/visao-geral',
        rotulo: 'Visão geral',
        descricao: 'S&OP × realizado no mês e programado × produzido por máquina.',
        icone: 'monitoring',
        gate: 'dashboard',
      },
      {
        rota: '/paineis/equiplex/maquinas',
        rotulo: 'Relatório Daily',
        descricao: 'Em abas: a daily por linha (sopro e envase) com a projeção do mês, e o MFR de cada máquina com as OPs sem apontamento.',
        icone: 'precision_manufacturing',
        gate: 'dashboard',
      },
      {
        rota: '/paineis/equiplex/sop',
        rotulo: 'S&OP e desvios',
        descricao: 'Programado nas OPs × produzido × faturado, por mês de produção.',
        icone: 'factory',
        gate: 'dashboard',
      },
      {
        rota: '/paineis/equiplex/vendas',
        rotulo: 'Forecast e vendas',
        descricao: 'Forecast do comercial × faturado por mês, SKU e UF de destino.',
        icone: 'storefront',
        gate: 'dashboard',
      },
      {
        rota: '/paineis/equiplex/dre',
        rotulo: 'DRE e desvios P&L',
        descricao: 'Contábil do Datasul mês a mês, contra o orçado e a média dos últimos meses.',
        icone: 'account_balance',
        gate: 'dashboard',
      },
      {
        rota: '/paineis/equiplex/qualidade',
        rotulo: 'Liberação da Qualidade',
        descricao: 'Fila do CQ, prazo de liberação por máquina e lotes pendentes.',
        icone: 'verified',
        gate: 'dashboard',
      },
      {
        rota: '/paineis/equiplex/apontamentos',
        rotulo: 'Apontamentos da reunião',
        descricao: 'O que a reunião registrou (paradas, faltas, problemas) e o PDF da reunião.',
        icone: 'edit_note',
        gate: 'dashboard',
      },
      {
        rota: '/relatorios/origem-dos-dados',
        rotulo: 'De onde vêm os dados',
        descricao: 'O caminho do Datasul até a tela e como cada número é calculado.',
        icone: 'account_tree',
        gate: 'dashboard',
      },
    ],
  },
  {
    id: 'planejamento',
    titulo: 'Planejamento',
    descricao: 'Capacidade e carga das linhas, com cenários editáveis.',
    itens: [
      {
        rota: '/balanceamento-linha',
        rotulo: 'Balanceamento de Linha',
        descricao: 'Demanda × capacidade por recurso, com cenários e cadastros.',
        icone: 'balance',
        gate: 'seop',
      },
    ],
  },
];

/** As flags do usuário que interessam para a navegação de relatórios. */
export interface GatesUsuario {
  dashboard?: boolean;
  seop?: boolean;
}

/** Um item só aparece se a pessoa puder abri-lo — link que dá 403 é armadilha. */
export function itemLiberado(item: ItemRelatorio, gates: GatesUsuario): boolean {
  return gates[item.gate] === true;
}

/**
 * O catálogo filtrado pelas flags. Grupo sem item liberado some inteiro, para a
 * home não mostrar título de seção vazia.
 */
export function gruposLiberados(gates: GatesUsuario): GrupoRelatorios[] {
  return GRUPOS_RELATORIOS.map((grupo) => ({
    ...grupo,
    itens: grupo.itens.filter((item) => itemLiberado(item, gates)),
  })).filter((grupo) => grupo.itens.length > 0);
}

/** Tem pelo menos um relatório para ver — é o que o guard de `/relatorios` checa. */
export function temAlgumRelatorio(gates: GatesUsuario): boolean {
  return gruposLiberados(gates).length > 0;
}
